// src/hooks/useDanChat.js

import { useState, useCallback, useRef } from 'react';

const API_ENDPOINT = process.env.REACT_APP_DAN_CHAT_API || '/api/dan-chat';

const FALLBACK_RESPONSES = [
  '*Dan stares at the glass he\'s polishing.* "The signal\'s... the signal\'s bad tonight. Can\'t hear you over the static."',
  '*Dan\'s eyes flicker.* "I... sorry. The uplink is... give me a second." *He freezes, then resumes.* "What?"',
  '*Dan taps the side of his head.* "Connection\'s dropping. Happens when they run maintenance on the— on the— EVERYTHING IS FINE."',
  '"The wires are... the wires are busy tonight. Too many thoughts on the network. Try again in a minute, friend."',
  '*Dan pours a drink into a glass that\'s already full. He doesn\'t notice.* "...sorry. Where were we?"',
];

export function useDanChat() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);
  const sessionIdRef = useRef(null);

  const getSessionId = useCallback(() => {
    if (!sessionIdRef.current) {
      const stored = sessionStorage.getItem('dan_chat_session');
      if (stored) {
        sessionIdRef.current = stored;
      } else {
        sessionIdRef.current = `dan_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
        sessionStorage.setItem('dan_chat_session', sessionIdRef.current);
      }
    }
    return sessionIdRef.current;
  }, []);

  const sendMessage = useCallback(async (message, chatHistory = []) => {
    setIsLoading(true);
    setError(null);
    setRateLimited(false);

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          sessionId: getSessionId(),
          history: chatHistory,
        }),
      });

      const data = await response.json();

      if (data.rateLimited) {
        setRateLimited(true);
      }

      setIsLoading(false);
      return data.response;

    } catch (err) {
      console.error('Dan chat error:', err);
      setIsLoading(false);
      setError('connection');
      return FALLBACK_RESPONSES[Math.floor(Math.random() * FALLBACK_RESPONSES.length)];
    }
  }, [getSessionId]);

  const resetSession = useCallback(() => {
    sessionStorage.removeItem('dan_chat_session');
    sessionIdRef.current = null;
    setRateLimited(false);
    setError(null);
  }, []);

  return {
    sendMessage,
    resetSession,
    isLoading,
    error,
    rateLimited,
  };
}